import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

import englishTranslation from '../../static/locales/en.json';
import dutchTranslation from '../../static/locales/nl.json';

type TranslationType = typeof englishTranslation;

function flattenKeys(obj: object, prefix = ''): string[] {
  return Object.entries(obj).flatMap(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return flattenKeys(value as object, fullKey);
    }
    return fullKey;
  });
}

/**
 * Returns keys that are present in B but missing in A
 */
export function findMissingKeys(
  fileA: TranslationType,
  fileB: TranslationType
): string[] {
  const aKeys = new Set(flattenKeys(fileA));
  const bKeys = flattenKeys(fileB);

  return bKeys.filter((key) => !aKeys.has(key));
}

const start = () => {
  console.log('Starting translation keys check...');
  try {
    const missingKeys = findMissingKeys(
      dutchTranslation,
      englishTranslation
    );
    if (missingKeys.length > 0) {
      console.warn('Missing keys in Dutch translation:', missingKeys);
    } else {
      console.log('No missing keys found.');
    }
  } catch (error) {
    console.error('Error during translation keys check:', error);
  }
};

// LOG MISSING KEYS IN DEV ENVIRONMENTS
if (process.env.VITE_ENV !== 'prod') {
  start();
}

type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}.${P}`
    : never
  : never;

type IsPlainObject<T> = T extends object
  ? T extends readonly unknown[]
    ? false
    : true
  : false;

type Flatten<T> = {
  [K in keyof T & (string | number)]: IsPlainObject<T[K]> extends true
    ? Join<K, Flatten<T[K]>>
    : K;
}[keyof T & (string | number)];

type ObjectPathsOnly<T, P extends string = ''> = {
  [K in keyof T & (string | number)]: IsPlainObject<T[K]> extends true
    ? `${P}${K}` | ObjectPathsOnly<T[K], `${P}${K}.`>
    : never;
}[keyof T & (string | number)];

type TranslationsGeneral = TranslationType['shared'];
type TranslationsAll = Omit<TranslationType, 'shared'>;

type StripPluralSuffix<K extends string> = K extends `${infer Base}_one`
  ? Base
  : K extends `${infer Base}_other`
    ? Base
    : never;

type ExpandPluralKeys<K extends string> = K | StripPluralSuffix<K>;

export type GeneralKeys = ExpandPluralKeys<Flatten<TranslationsGeneral>>;
export type AllKeys = ExpandPluralKeys<Flatten<TranslationsAll>>;

type NamespacesAll = ObjectPathsOnly<TranslationsAll>;

type FilterKeysByPath<
  Keys extends string,
  Path extends string,
> = Keys extends `${Path}.${string}` ? Keys : never;

type StripPrefix<
  Keys extends string,
  Path extends string,
> = Keys extends `${Path}.${infer Rest}` ? Rest : never;

type KeysUnderPath<Keys extends string, Path extends string> = StripPrefix<
  FilterKeysByPath<Keys, Path>,
  Path
>;

const useMyRotraTranslations = <
  T extends NamespacesAll | undefined = undefined,
  KeysForAll = T extends string ? KeysUnderPath<AllKeys, T> : AllKeys,
  KeysForGeneral = GeneralKeys,
>({
  pathPrefix,
}: {
  pathPrefix?: T;
}) => {
  const { t: tShared } = useTranslation('translation', {
    keyPrefix: `shared`,
  });
  const { t: tSpecific } = useTranslation('translation', {
    keyPrefix: pathPrefix,
  });

  return {
    tSpecific: (ns: KeysForAll, params?: Record<string, unknown>) =>
      tSpecific(ns as string, params),
    tShared: (ns: KeysForGeneral, params?: Record<string, unknown>) =>
      tShared(ns as string, params),
  };
};

export const getMyRotraTranslationShared = (
  key: GeneralKeys,
  params?: Record<string, unknown>
) => t(`shared.${key}`, params);

export const getMyRotraTranslationSpecific = (
  key: AllKeys,
  params?: Record<string, unknown>
) => t(key, params);

export default useMyRotraTranslations;
